import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { deleteUserCertificate, useAppDispatch } from "../../../redux";
import Spinner from "../../shared/components/Spinner";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Certification {
    id?: string;
    name: string;
    issuer: string;
    issueDate: string;
    expiryDate: string;
}

interface NewCertificateModalProps {
    open: boolean;
    onClose: () => void;
    onSave: any;
    initialData: any;
}
const emptyEntry = (): Certification => ({
    name: "",
    issuer: "",
    issueDate: "",
    expiryDate: "",
});
const NewCertificateModal: React.FC<NewCertificateModalProps> = ({ open, onSave, onClose, initialData }) => {
    const { t } = useTranslation();
    const [certificates, setCertificates] = useState<Certification[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const dispatch = useAppDispatch();
    const { user } = useSelector((state: any) => state.auth);
    const isLoading = useSelector(selectLoaderValue);

    const handleChange = (index: number, field: keyof Certification, value: string) => {
        const updated = [...certificates];
        updated[index][field] = value;
        setCertificates(updated);
        setErrorMessage(""); // clear on input
    };

    const addNewCertification = () => {
        setCertificates([...certificates, { name: "", issuer: "", issueDate: "", expiryDate: "" }]);
        setErrorMessage(""); // Clear error
    };

    const removeCertification = async (index: number) => {
        const certificate = certificates[index];
        if (certificate?.id) {
            await dispatch(deleteUserCertificate({ id: certificate.id }));
        }

        const updated = [...certificates];
        updated.splice(index, 1);
        setCertificates(updated);
    };

    const handleSave = async () => {
        const isValid = certificates.every(
            cert => cert.name.trim() && cert.issuer.trim() && cert.issueDate.trim() && cert.expiryDate.trim()
        );

        if (!isValid) {
            setErrorMessage(t("newCertificateModal.errors.fillAllFields"));
            return;
        }

        if (certificates.length === 0) {
            setErrorMessage(t("newCertificateModal.errors.addAtLeastOne"));
            return;
        }
        onSave(certificates);
        onClose();
    };
    useEffect(() => {
        if (initialData?.length > 0) {
            setCertificates(initialData.map(item => ({ ...item }))); // shallow clone
        } else {
            setCertificates([emptyEntry()]);
        }
    }, [open, initialData]);
    useEffect(() => {
        if (open && certificates.length === 0) {
            setCertificates([{ name: "", issuer: "", issueDate: "", expiryDate: "" }]);
        }
    }, [open]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">{t("newCertificateModal.title")}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
                        &times;
                    </button>
                </div>

                {/* Add New Button */}
                <button
                    className="bg-blue-600 text-white px-4 py-2 rounded-md mb-4 hover:bg-blue-700"
                    onClick={addNewCertification}>
                    {t("newCertificateModal.addNew")}
                </button>

                {/* Certification Forms */}
                {certificates.map((cert, index) => (
                    <div key={index} className="border border-gray-300 rounded-md p-4 mb-4 relative">
                        <div className="flex justify-between items-start">
                            <h4 className="font-medium mb-2">{t("newCertificateModal.certificationNumber")} {index + 1}</h4>
                            <button
                                onClick={() => removeCertification(index)}
                                className="text-gray-400 hover:text-gray-600">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <label className="block text-sm font-medium mb-1">{t("newCertificateModal.name")}</label>
                        <input
                            type="text"
                            placeholder={t("newCertificateModal.namePlaceholder")}
                            value={cert.name}
                            onChange={e => handleChange(index, "name", e.target.value)}
                            className="w-full mb-2 p-2 border rounded-md"
                        />

                        <label className="block text-sm font-medium mb-1">{t("newCertificateModal.issuer")}</label>
                        <input
                            type="text"
                            placeholder={t("newCertificateModal.issuerPlaceholder")}
                            value={cert.issuer}
                            onChange={e => handleChange(index, "issuer", e.target.value)}
                            className="w-full mb-2 p-2 border rounded-md"
                        />

                        <label className="block text-sm font-medium mb-1">{t("newCertificateModal.dateIssued")}</label>
                        <input
                            type="number"
                            placeholder={t("newCertificateModal.dateIssuedPlaceholder")}
                            value={cert.issueDate}
                            onChange={e => handleChange(index, "issueDate", e.target.value)}
                            className="w-full mb-2 p-2 border rounded-md"
                            min={1900}
                            max={2100}
                        />

                        <label className="block text-sm font-medium mb-1">{t("newCertificateModal.expiryDate")}</label>
                        <input
                            type="number"
                            placeholder={t("newCertificateModal.expiryDatePlaceholder")}
                            value={cert.expiryDate}
                            onChange={e => handleChange(index, "expiryDate", e.target.value)}
                            className="w-full p-2 border rounded-md"
                            min={1900}
                            max={2100}
                        />
                    </div>
                ))}

                {/* Error Message */}
                {errorMessage && <div className="text-red-600 text-sm mb-4">{errorMessage}</div>}

                {/* Footer */}
                {/* Footer: Only show when there are certificates */}
                {certificates.length > 0 && (
                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100">
                            {t("newCertificateModal.close")}
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-800 flex">
                            {isLoading ? (
                                <>
                                    <Spinner size={5} />
                                    {t("newCertificateModal.loading")}
                                </>
                            ) : (
                                t("newCertificateModal.saveChanges")
                            )}{" "}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewCertificateModal;
